import { useSignUp } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function SignUpPage() {
  const { signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");

  const handleGoogleSignUp = async () => {
    if (!signUp) return;
    setErrorMessage("");
    try {
      await signUp.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/",
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to start Google sign-up.";
      setErrorMessage(message);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp || !setActive) return;
    setErrorMessage("");
    setSubmitting(true);
    try {
      const result = await signUp.create({ emailAddress: email, password });

      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate("/", { replace: true });
        return;
      }

      // Clerk asks for email verification — send the code and switch states.
      if (result.status === "missing_requirements") {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setVerifying(true);
        return;
      }

      setErrorMessage("We couldn't finish your sign-up. Please try again.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to sign up. Please try again.";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp || !setActive) return;
    setErrorMessage("");
    setSubmitting(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      if (result.status === "complete" && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        navigate("/", { replace: true });
        return;
      }
      setErrorMessage("The verification code didn't match. Please try again.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Verification failed. Please try again.";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sidebar via-sidebar to-emerald-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-md p-12 rounded-2xl shadow-2xl max-w-md w-full border border-white/20">
        <div className="flex flex-col items-center text-center">
          <div className="mb-6">
            <img src="/dfr-logo.svg" alt="DFR Logo" className="h-12 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Dominion Programme</h1>
          <p className="text-white/60 mb-8">Create your account.</p>

          {!verifying && (
            <>
              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={busy}
                className="w-full bg-white text-sidebar px-6 py-3 rounded-xl font-bold hover:bg-white/90 transition-colors flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed mb-4"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              <div className="w-full mb-4 flex items-center gap-3">
                <div className="flex-1 border-t border-white/20" />
                <span className="text-white/40 text-sm">or</span>
                <div className="flex-1 border-t border-white/20" />
              </div>

              <form onSubmit={handlePasswordSubmit} className="w-full space-y-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                <input
                  type="password"
                  placeholder="Choose a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40"
                />
                {errorMessage && <p className="text-red-400 text-sm text-left">{errorMessage}</p>}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create account
                </button>
              </form>
            </>
          )}

          {verifying && (
            <form onSubmit={handleCodeSubmit} className="w-full space-y-3">
              <p className="text-white/60 text-sm mb-2">
                We've emailed a verification code to <span className="text-white">{email}</span>.
              </p>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40 tracking-widest text-center"
              />
              {errorMessage && <p className="text-red-400 text-sm text-left">{errorMessage}</p>}
              <button
                type="submit"
                disabled={busy || !code}
                className="w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify email
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 w-full text-center">
            <p className="text-white/60 text-sm mb-2">Already have an account?</p>
            <Link
              to="/sign-in"
              className="inline-block w-full bg-transparent border border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
